import { useEffect, useState, useCallback, useRef } from 'react';
import { SignaturePad } from '@/components/common/SignaturePad';
import { printAod } from '@/components/staff/aod';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HandCoins, Check, X, Download, Clock3, CheckCircle2, XCircle, Plus, Upload, FileText, Trash2, Paperclip, Printer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const badge = (s) => {
  if (s === 'approved') return <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>;
  if (s === 'declined') return <Badge className="bg-rose-600"><XCircle className="h-3 w-3 mr-1" />Declined</Badge>;
  return <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50"><Clock3 className="h-3 w-3 mr-1" />Pending</Badge>;
};

export default function StaffLoansAdmin() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Template
  const [tpl, setTpl] = useState({ available: false });
  const tplInputRef = useRef(null);

  // Create-loan dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [createForm, setCreateForm] = useState({ staff_id: '', amount: '1000', reason: '', auto_approve: true });
  const [createFile, setCreateFile] = useState(null);
  const [creating, setCreating] = useState(false);

  // Per-loan countersign upload
  const [uploadingId, setUploadingId] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rL, rT] = await Promise.all([
        axios.get(`${API}/staff-loans`),
        axios.get(`${API}/staff-loans/template/info`),
      ]);
      setLoans(rL.data || []);
      setTpl(rT.data || { available: false });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  const fetchEligible = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/staff-loans/eligible-staff`);
      setEligible(r.data || []);
    } catch (e) { toast.error('Failed to load staff list'); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (createOpen) fetchEligible(); }, [createOpen, fetchEligible]);

  const sigRef = useRef(null);
  const submitReview = async () => {
    if (!reviewing) return;
    const approving = reviewing.decision === 'approve';
    if (approving && sigRef.current?.isBlank()) { toast.error('Please sign to countersign the AOD'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/staff-loans/${reviewing.loan.id}/approve`, {
        decision: reviewing.decision,
        note: reviewing.note,
        ...(approving ? { company_signature: sigRef.current.toDataURL() } : {}),
      });
      toast.success(reviewing.decision === 'approve' ? 'Approved · loan added to their next payslip' : 'Declined');
      setReviewing(null); await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const download = async (l) => {
    try {
      const r = await axios.get(`${API}/staff-loans/${l.id}/agreement-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Loan_Agreement_${(l.staff_name || 'staff').replace(/\s+/g, '_')}_${l.id.slice(0,8)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const downloadSigned = async (l) => {
    try {
      const r = await axios.get(`${API}/staff-loans/${l.id}/signed-agreement`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Signed_${(l.staff_name || 'staff').replace(/\s+/g, '_')}_${l.id.slice(0,8)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const uploadCountersign = async (l, file) => {
    if (!file) return;
    setUploadingId(l.id);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      await axios.post(`${API}/staff-loans/${l.id}/signed-agreement`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Signed agreement uploaded');
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingId(''); }
  };

  const deleteCountersign = async (l) => {
    if (!window.confirm(`Remove uploaded agreement for ${l.staff_name}?`)) return;
    try {
      await axios.delete(`${API}/staff-loans/${l.id}/signed-agreement`);
      toast.success('Removed');
      await fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const uploadTemplate = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      await axios.post(`${API}/staff-loans/template`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Template uploaded — staff can now download it');
      const r = await axios.get(`${API}/staff-loans/template/info`);
      setTpl(r.data || { available: false });
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { if (tplInputRef.current) tplInputRef.current.value = ''; }
  };

  const deleteTemplate = async () => {
    if (!window.confirm('Delete the current template agreement?')) return;
    try {
      await axios.delete(`${API}/staff-loans/template`);
      toast.success('Template removed');
      setTpl({ available: false });
    } catch { toast.error('Delete failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/staff-loans/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = tpl.filename || 'Staff_Loan_Agreement_Template.pdf';
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const submitCreate = async () => {
    if (!createForm.staff_id) { toast.error('Pick a staff member'); return; }
    const amt = Number(createForm.amount);
    if (!amt || amt <= 0 || amt > 1000) { toast.error('Amount must be R1 – R1 000'); return; }
    if (!createForm.reason.trim()) { toast.error('Reason is required'); return; }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append('staff_id', createForm.staff_id);
      fd.append('amount', String(amt));
      fd.append('reason', createForm.reason.trim());
      fd.append('auto_approve', String(!!createForm.auto_approve));
      if (createFile) fd.append('signed_agreement', createFile);
      await axios.post(`${API}/staff-loans/admin-create`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(createForm.auto_approve ? 'Loan created & approved' : 'Loan created (pending approval)');
      setCreateOpen(false);
      setCreateForm({ staff_id: '', amount: '1000', reason: '', auto_approve: true });
      setCreateFile(null);
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setCreating(false); }
  };

  const pending = loans.filter((l) => l.status === 'pending');
  const decided = loans.filter((l) => l.status !== 'pending');

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <HandCoins className="h-6 w-6 text-emerald-600" /> Staff Loan Requests
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Review, approve or decline staff loan requests (up to R1&nbsp;000). Approved loans are added to `loan_balance` and auto-deducted from the next payslip.
            </p>
          </div>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-create-loan-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Create Loan
          </Button>
        </div>

        {pending.length > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
            <span>
              <b>{pending.length}</b> pending loan{pending.length === 1 ? '' : 's'}. Admins are auto-pinged every 24h any request older than a day sits unhandled.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500 text-amber-900 hover:bg-amber-100 h-7"
              data-testid="scan-overdue-btn"
              onClick={async () => {
                try {
                  const r = await axios.post(`${API}/staff-loans/scan-overdue`);
                  const { scanned, notifications_sent } = r.data || {};
                  if (scanned) toast.success(`Reminded ${notifications_sent} admin bell(s) about ${scanned} overdue loan${scanned === 1 ? '' : 's'}`);
                  else toast.info('No overdue loans right now.');
                } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
              }}
            >
              <Clock3 className="h-3 w-3 mr-1" /> Scan Overdue Now
            </Button>
          </div>
        )}

        {/* Template card */}
        <Card data-testid="template-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" /> Loan Agreement Template
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm">
              {tpl.available ? (
                <>
                  <div className="font-medium">{tpl.filename || 'template.pdf'}</div>
                  <div className="text-xs text-muted-foreground">
                    {Math.round((tpl.size || 0) / 1024)} KB · uploaded {tpl.uploaded_at ? new Date(tpl.uploaded_at).toLocaleDateString('en-ZA') : ''}
                    {tpl.uploaded_by ? ` by ${tpl.uploaded_by}` : ''}
                  </div>
                </>
              ) : (
                <div className="text-muted-foreground italic">
                  No template uploaded yet. Upload one PDF and staff can download / print / hand-sign it.
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {tpl.available && (
                <>
                  <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="template-download-btn">
                    <Download className="h-3 w-3 mr-1" /> Download
                  </Button>
                  <Button variant="outline" size="sm" className="border-rose-400 text-rose-700" onClick={deleteTemplate} data-testid="template-delete-btn">
                    <Trash2 className="h-3 w-3 mr-1" /> Remove
                  </Button>
                </>
              )}
              <input
                ref={tplInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => uploadTemplate(e.target.files?.[0])}
                data-testid="template-file-input"
              />
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => tplInputRef.current?.click()} data-testid="template-upload-btn">
                <Upload className="h-3 w-3 mr-1" /> {tpl.available ? 'Replace' : 'Upload Template'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pending list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-amber-500" /> Pending ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="text-center py-6 text-muted-foreground">Loading…</div>
            ) : pending.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">No pending requests 🎉</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map((l) => (
                    <LoanRow
                      key={l.id}
                      loan={l}
                      isPending
                      uploadingId={uploadingId}
                      onDownload={() => download(l)}
                      onDownloadSigned={() => downloadSigned(l)}
                      onUploadSigned={(f) => uploadCountersign(l, f)}
                      onDeleteSigned={() => deleteCountersign(l)}
                      onApprove={() => setReviewing({ loan: l, decision: 'approve', note: '' })}
                      onDecline={() => setReviewing({ loan: l, decision: 'decline', note: '' })}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* History */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">History ({decided.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {decided.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">Nothing yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reviewed by</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead className="text-right">PDF</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decided.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="font-medium">
                        {l.staff_name}
                        {l.created_by_admin && <div className="text-[10px] text-blue-600">Admin-raised</div>}
                      </TableCell>
                      <TableCell className="font-bold">{R(l.amount)}</TableCell>
                      <TableCell>{badge(l.status)}</TableCell>
                      <TableCell className="text-xs">
                        {l.reviewed_by || '—'}
                        {l.reviewed_at && <div className="text-[10px] text-muted-foreground">{new Date(l.reviewed_at).toLocaleString('en-ZA')}</div>}
                      </TableCell>
                      <TableCell className="text-xs italic text-muted-foreground max-w-xs truncate">{l.review_note || '—'}</TableCell>
                      <TableCell>
                        <DocsCell
                          loan={l}
                          uploadingId={uploadingId}
                          onUpload={(f) => uploadCountersign(l, f)}
                          onDownload={() => downloadSigned(l)}
                          onDelete={() => deleteCountersign(l)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="mr-1" onClick={() => printAod(l.id)} data-testid={`print-aod-decided-${l.id}`}>
                          <Printer className="h-3 w-3 mr-1" /> AOD
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => download(l)}>
                          <Download className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Review dialog */}
        <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
          <DialogContent data-testid="loan-review-dialog">
            <DialogHeader>
              <DialogTitle>{reviewing?.decision === 'approve' ? 'Approve' : 'Decline'} loan</DialogTitle>
              <DialogDescription>
                {reviewing?.decision === 'approve'
                  ? <>Approving {R(reviewing?.loan?.amount)} for <b>{reviewing?.loan?.staff_name}</b>. Their `loan_balance` will be increased by this amount and their next payslip's `loan_opening_balance` will pick it up automatically.</>
                  : <>Declining {R(reviewing?.loan?.amount)} for <b>{reviewing?.loan?.staff_name}</b>. Optionally add a note.</>
                }
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={3}
              value={reviewing?.note || ''}
              onChange={(e) => setReviewing({ ...reviewing, note: e.target.value })}
              placeholder="Optional note (visible to the staff member)"
              data-testid="review-note"
            />
            {reviewing?.decision === 'approve' && (
              <SignaturePad ref={sigRef} testId="admin-aod-signature" label="Sign for JSM Transport (countersigns the AOD) *" />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewing(null)}>Cancel</Button>
              <Button onClick={submitReview} disabled={saving}
                      className={reviewing?.decision === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
                      data-testid="review-confirm">
                {saving ? 'Saving…' : reviewing?.decision === 'approve' ? 'Approve loan' : 'Decline loan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create loan dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md" data-testid="admin-create-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><HandCoins className="h-4 w-4 text-emerald-600" /> Raise loan for staff</DialogTitle>
              <DialogDescription>
                Create a loan on behalf of a staff member who can't sign in. Optionally attach their signed paper agreement.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Staff *</Label>
                <Select value={createForm.staff_id} onValueChange={(v) => setCreateForm({ ...createForm, staff_id: v })}>
                  <SelectTrigger data-testid="create-loan-staff-select"><SelectValue placeholder="Pick a staff member" /></SelectTrigger>
                  <SelectContent>
                    {eligible.length === 0 && <div className="p-3 text-xs text-muted-foreground">No eligible staff (internal_staff role)</div>}
                    {eligible.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.full_name || s.username}
                        {Number(s.loan_balance || 0) > 0 && <span className="ml-2 text-xs text-rose-500">· R{s.loan_balance} outstanding</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Amount (R) *</Label>
                <Input
                  type="number" min="1" max="1000"
                  value={createForm.amount}
                  onChange={(e) => setCreateForm({ ...createForm, amount: e.target.value })}
                  data-testid="create-loan-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Reason *</Label>
                <Textarea
                  rows={2}
                  value={createForm.reason}
                  onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                  placeholder="e.g. School fees advance"
                  data-testid="create-loan-reason"
                />
              </div>
              <div>
                <Label className="text-xs">Signed agreement PDF (optional)</Label>
                <Input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setCreateFile(e.target.files?.[0] || null)}
                  data-testid="create-loan-signed-file"
                />
                {createFile && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Selected: {createFile.name} ({Math.round(createFile.size / 1024)} KB)
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={createForm.auto_approve}
                  onChange={(e) => setCreateForm({ ...createForm, auto_approve: e.target.checked })}
                  data-testid="create-loan-auto-approve"
                />
                Approve immediately (add to their loan balance now)
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={submitCreate}
                disabled={creating}
                data-testid="create-loan-submit"
              >
                {creating ? 'Saving…' : 'Create loan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function LoanRow({ loan, isPending, uploadingId, onDownload, onDownloadSigned, onUploadSigned, onDeleteSigned, onApprove, onDecline }) {
  return (
    <TableRow data-testid={`pending-${loan.id}`}>
      <TableCell>
        <div className="font-medium">{loan.staff_name}</div>
        <div className="text-[10px] text-muted-foreground">
          {(loan.role || '').replace('_', ' ')}
          {loan.created_by_admin && <span className="ml-1 text-blue-600">· Admin-raised</span>}
        </div>
      </TableCell>
      <TableCell className="font-bold text-emerald-700">{R(loan.amount)}</TableCell>
      <TableCell className="max-w-xs text-xs">{loan.reason || '—'}</TableCell>
      <TableCell className="text-xs">{new Date(loan.requested_at).toLocaleString('en-ZA')}</TableCell>
      <TableCell>
        <DocsCell
          loan={loan}
          uploadingId={uploadingId}
          onUpload={onUploadSigned}
          onDownload={onDownloadSigned}
          onDelete={onDeleteSigned}
        />
      </TableCell>
      <TableCell className="text-right space-x-1">
        <Button size="sm" variant="outline" onClick={() => printAod(loan.id)} data-testid={`print-aod-${loan.id}`}>
          <Printer className="h-3 w-3 mr-1" /> AOD
        </Button>
        <Button size="sm" variant="outline" onClick={onDownload} data-testid={`view-pdf-${loan.id}`}>
          <Download className="h-3 w-3 mr-1" /> PDF
        </Button>
        {isPending && (
          <>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={onApprove}
                    data-testid={`approve-${loan.id}`}>
              <Check className="h-3 w-3 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" className="border-rose-400 text-rose-700 hover:bg-rose-50"
                    onClick={onDecline}
                    data-testid={`decline-${loan.id}`}>
              <X className="h-3 w-3 mr-1" /> Decline
            </Button>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

function DocsCell({ loan, uploadingId, onUpload, onDownload, onDelete }) {
  const inputRef = useRef(null);
  const busy = uploadingId === loan.id;
  const hasDoc = !!loan.signed_agreement_uploaded_at;
  return (
    <div className="flex items-center gap-1">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ''; } }}
        data-testid={`signed-input-${loan.id}`}
      />
      {hasDoc ? (
        <>
          <Button size="sm" variant="outline" onClick={onDownload} title="Download signed" data-testid={`signed-download-${loan.id}`}>
            <FileText className="h-3 w-3 text-emerald-600" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} title="Replace" disabled={busy} data-testid={`signed-replace-${loan.id}`}>
            <Upload className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="outline" className="border-rose-300" onClick={onDelete} title="Remove" data-testid={`signed-delete-${loan.id}`}>
            <Trash2 className="h-3 w-3 text-rose-600" />
          </Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`signed-upload-${loan.id}`}>
          {busy ? '…' : <><Paperclip className="h-3 w-3 mr-1" />Upload</>}
        </Button>
      )}
    </div>
  );
}
